import { Env } from "@ancademy/vse-client";
import { Color, Md, showMsg } from "@client";
import { Style } from "@client/style";
import { downloadJsonAsCsv, loremIpsum, useAsyncEffect } from "@client/util";
import { css } from "@emotion/css";
import { Button, Modal, Radio } from "antd";
import React, { createContext, useContext } from "react";
import style from "./style.scss";

interface ITestPageQuestion {
  Content: React.ComponentType<{
    inputProps: (style?: React.CSSProperties) => {
      style: React.CSSProperties;
      value;
      onChange;
    };
  }>;
  Answer?: React.ComponentType;
  answer?: Array<React.ReactText | React.ReactText[]>;
  isAnalysis?: boolean;
}

interface IQuestionProps extends ITestPageQuestion {
  onChange: (answers: React.ReactText[]) => void;
}

function Question({ Content, Answer, answer, onChange }: IQuestionProps) {
  const [inputArr, _setInputArr] = React.useState([]);
  const [showAnswer, setShowAnswer] = React.useState(false);
  let inputIndex = 0;

  function inputProps(style: React.CSSProperties = { width: "8rem", margin: "2px .5rem" }) {
    const index = inputIndex++;
    return {
      style,
      value: inputArr[index],
      disabled: showAnswer,
      onChange: (valueOrEvent) => {
        const _inputArr = inputArr.slice();
        _inputArr[index] = valueOrEvent?.target?.value || valueOrEvent;
        _setInputArr(_inputArr);
      },
    };
  }

  return (
    <section className={style.question}>
      <div className={style.contentWrapper}>
        <Content inputProps={(style) => inputProps(style)} />
      </div>
      {Answer ? (
        <>
          <div className={`${style.answerWrapper} ${showAnswer ? style.active : ""}`}>
            答案解析： <Answer />
          </div>
          <div className={style.btnWrapper}>
            {showAnswer ? (
              <Button className={Style.btnPrimarySm} onClick={() => onChange(inputArr)}>
                下一步
              </Button>
            ) : (
              <Button className={Style.btnPrimarySm} onClick={() => setShowAnswer(true)}>
                确定
              </Button>
            )}
          </div>
        </>
      ) : (
        <div className={style.btnWrapper}>
          <Button className={Style.btnPrimarySm} onClick={() => onChange(inputArr)}>
            下一步
          </Button>
        </div>
      )}
    </section>
  );
}

export type TTestPageCtx = {
  done: (answers: React.ReactText[][], answerScores: number[]) => void;
  getDownloadData?: () => Promise<any[]>;
};

export const testPageCtx = createContext<TTestPageCtx>({
  done: () => {},
  getDownloadData: async () => [{ id: loremIpsum(24) }],
});

export function TestPage({ title = "小测验", questions }: { title?: string; questions: Array<ITestPageQuestion> }) {
  if (!questions.length) {
    questions = [
      {
        Content: ({ inputProps }) => (
          <div>
            <p>待补充测试题，1+1=?</p>
            <Radio.Group {...inputProps({ margin: ".5rem" })}>
              <Radio value={1}>A. 1</Radio>
              <Radio value={2}>B. 2</Radio>
              <Radio value={3}>C. 3</Radio>
              <Radio value={4}>D. 4</Radio>
            </Radio.Group>
          </div>
        ),
        Answer: () => <p>1+1=2</p>,
        answer: [2],
      },
    ];
  }
  const [questionIndex, setQuestionIndex] = React.useState(0),
    question = questions[questionIndex];
  const [answers, setAnswers] = React.useState([] as React.ReactText[][]);
  const { done, getDownloadData } = useContext(testPageCtx);
  useAsyncEffect(async () => {
    if (question.isAnalysis) {
      await showMsg(`现在，你可以下载本环节实验数据，请分析它们并自由地回答主观题。`, { okText: "知道了" });
    }
  }, [question]);
  const subTitleStyle = css`
    font-size: 1.5rem;
    color: ${Color.main};
  `;
  return (
    <Modal {...Md.pageProps} footer={false}>
      <h2
        className={css`
          text-align: center;
          font-size: 2rem;
          color: ${Color.main};
        `}
      >
        {question.isAnalysis
          ? "数据分析"
          : `${title}(${questionIndex + 1}/${questions.filter((s) => !s.isAnalysis).length})`}
      </h2>
      {question.isAnalysis && (
        <>
          <div
            className={css`
              display: flex;
              margin-bottom: 1.5rem;
              gap: 1rem;
            `}
          >
            <h3 className={subTitleStyle}>实验数据：</h3>
            <div>
              <Button
                className={Style.btnPrimaryXS}
                onClick={async () => {
                  downloadJsonAsCsv(await getDownloadData(), `${location.href.split(Env.basePath + "/")[1]}.csv`);
                }}
              >
                下载
              </Button>
            </div>
          </div>
          <h3 className={subTitleStyle}>分析：</h3>
        </>
      )}
      <Question
        key={questionIndex}
        onChange={(newAnswers) => {
          const _answers = [...answers];
          _answers[questionIndex] = newAnswers;
          setAnswers(_answers);
          if (questionIndex < questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
          } else {
            done(
              _answers,
              _answers.map((_answer, i) => {
                const { answer } = questions[i];
                if (answer) {
                  //此函数临时用于多选题结果判断
                  const toStr = (a) => JSON.stringify(a).split("").sort().join();
                  return toStr(_answer) === toStr(answer) ? 10 : 0;
                } else {
                  return _answer.length ? 10 : 0;
                }
              })
            );
          }
        }}
        {...question}
      />
    </Modal>
  );
}
